import { ChooseMediaModal } from '@components/ChooseMediaModal/ChooseMediaModal';
import { ChooseMediaModalSummaryConfirm } from '@components/ChooseMediaModal/ChooseMediaModalSummaryConfirm';
import { MediaUploadBox } from '@components/MediaUploadBox/MediaUploadBox';
import {
  isFacebookAdsMultipleData,
  isGoogleDriveFolderData,
  isYoutubeMultipleData,
} from '@lib/external/unfurl';
import { useUrlUploadFlow } from '@lib/hooks/useUrlUploadFlow';
import { FOLDER_ROOT_PATH, type FolderPath } from '@lib/library/folderPath';
import { estimateIngest } from '@lib/utils/ingestEstimate';
import { showToast } from '@lib/utils/toastmaster';
import { getLibraryUrl } from '@lib/utils/url';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import {
  DuplicateDetectionService,
  ExternalMediaItem,
} from '@services/Upload/DuplicateDetectionService';
import { billingStore } from '@stores/Billing';
import { debugStore } from '@stores/Debug';
import { Library } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { useUploadURL } from '@stores/UploadURL/UploadURLStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UrlImportComponentProps {
  className?: string;
  closeModal?: () => void;
  isFirstTime?: boolean;
  externalIsDragging?: boolean;
  autoFocus?: boolean;
  compact?: boolean;
  /** When the chosen destination is a brand-new library: creates it on confirm. */
  createNewLibrary?: boolean;
  /** Name for the new library (entered next to the destination picker). */
  newLibraryName?: string;
  /** Portal target (modal footer) for the action button; see MediaUploadBox. */
  footerPortalTarget?: HTMLElement | null;
  /** Gate run before any upload proceeds (e.g. require the new-library name).
   *  Returns false to abort. */
  validateBeforeProceed?: () => boolean;
  /** The folder of the destination library uploads are filed in; its root when absent. */
  folderPath?: FolderPath;
}

export const UrlImportComponent = observer(
  ({
    className,
    closeModal,
    isFirstTime,
    externalIsDragging,
    autoFocus,
    compact,
    createNewLibrary,
    newLibraryName,
    footerPortalTarget,
    validateBeforeProceed,
    folderPath = FOLDER_ROOT_PATH,
  }: UrlImportComponentProps) => {
    const uploadURLStore = useUploadURL();
    const uploadManager = useUploadManager();
    const libraryManager = useLibraryManager();

    const [uploadProgress, setUploadProgress] = useState<{
      completed: number;
      total: number;
    } | null>(null);

    const libraryFolder = useLibraryFolder();
    const { library } = libraryFolder;
    const navigate = useNavigate();
    const location = useLocation();

    // The chosen destination is a new library (picker) or legacy first-time auto-create.
    const shouldCreateNewLibrary = Boolean(createNewLibrary || isFirstTime);

    // Create DuplicateDetectionService instance
    const duplicateService = useMemo(() => new DuplicateDetectionService(), []);

    // Reference-stable across unrelated re-renders (MobX computed) — rebuilding
    // this per render gave the grid new row identities on every library-folder
    // tick and reset the user's scroll. See UploadURLStore.unfurledItems.
    const unfurledItems = uploadURLStore.unfurledItems;

    const urlUploadFlow = useUrlUploadFlow({
      unfurledItems,
      onUploadButtonClick: handleChooseMediaUploadButtonClick,
      createNewLibrary: shouldCreateNewLibrary,
      newLibraryName,
      onCancel: () => closeModal?.(),
      uploadProgress,
    });

    // Defined after urlUploadFlow but only called asynchronously (user confirms upload),
    // so the hook reference is always initialized by the time this runs.
    async function handleChooseMediaUploadButtonClick(
      selectedVideoItems: {
        url: string;
        name: string;
        userMetadata?: Record<string, any>;
        uniqueId?: string;
      }[],
      libraryOptions?: {
        createNewLibrary: boolean;
        newLibraryName: string;
        abortController?: AbortController;
      },
    ) {
      let newLibrary: Library | null = null;

      try {
        if (selectedVideoItems.length === 0) {
          return;
        }

        // Google Drive folder files carry a real MIME type in the enhanced
        // item's `type` (e.g. "image/jpeg"); YouTube items use the literal
        // "video" (no "/"). Re-validate the MIME-typed (folder) selections here
        // against the accepted media types, since the selected items no longer
        // carry their MIME by this point. Unsupported files are skipped and
        // surfaced as a rich-event card, matching file-upload rejects.
        const mimeByUrl = new Map<string, string>();
        for (const enhanced of uploadURLStore.allEnhancedVideos) {
          if (typeof enhanced.type === 'string' && enhanced.type.includes('/')) {
            mimeByUrl.set(enhanced.url, enhanced.type);
          }
        }
        const folderRejected: { filename: string; reason: string }[] = [];
        const supportedSelectedItems = selectedVideoItems.filter((item) => {
          const mimeType = mimeByUrl.get(item.url);
          if (!mimeType) return true; // not a Drive-folder file → leave untouched
          const validation = uploadManager.isAcceptedExternalMedia({
            mimeType,
            filename: item.name,
          });
          if (!validation.accepted) {
            folderRejected.push({
              filename: item.name || 'Untitled',
              reason: validation.reason || 'Unsupported file type',
            });
            return false;
          }
          return true;
        });
        if (folderRejected.length > 0) {
          uploadManager.pushFolderRejectedRichEvent(folderRejected);
        }
        if (supportedSelectedItems.length === 0) {
          closeModal?.();
          return;
        }

        // Convert to ExternalMediaItem format
        const externalMediaItems: ExternalMediaItem[] = supportedSelectedItems.map((item) => ({
          type: 'external' as const,
          url: item.url,
          name: item.name,
          userMetadata: item.userMetadata,
          uniqueId: item.uniqueId,
        }));

        // Filter out duplicates using DuplicateDetectionService. A brand-new
        // library has no existing media, so dedup against an empty set.
        const nonDuplicateItems = await duplicateService.filterDuplicates(
          externalMediaItems,
          shouldCreateNewLibrary ? {} : libraryFolder.uniqueIds,
          {
            existingExternalMediaWithHashes: uploadURLStore.allEnhancedVideos,
            autoGenerateHashes: true,
          },
        );

        const nonDuplicateExternalItems = nonDuplicateItems.filter(
          (item): item is ExternalMediaItem => item.type === 'external',
        );

        const nonDuplicateSelectedItems: {
          url: string;
          name: string;
          userMetadata?: Record<string, any>;
          uniqueId?: string;
        }[] = nonDuplicateExternalItems.map((item) => ({
          url: item.url,
          name: item.name,
          userMetadata: item.userMetadata,
          uniqueId: item.uniqueId,
        }));

        if (nonDuplicateSelectedItems.length === 0) {
          closeModal?.();
          return;
        }

        // Handle library creation based on options from ChooseMediaModal
        if (libraryOptions?.createNewLibrary) {
          newLibrary = await libraryManager.createNewLibrary(libraryOptions.newLibraryName);
        }

        const abortController = libraryOptions?.abortController || new AbortController();

        const destinationLibraryId = newLibrary ? newLibrary._id : library.libraryId;

        // Track Google Drive folder imports through the picker (multi-file path).
        const isGoogleDriveFolderImport = uploadURLStore.validEntries.some(
          (e) => e.unfurlData && isGoogleDriveFolderData(e.unfurlData),
        );
        if (isGoogleDriveFolderImport) {
          trackingStore.googleDriveFolderImported({
            libraryId: destinationLibraryId,
            fileCount: nonDuplicateSelectedItems.length,
            skippedCount: folderRejected.length,
            source: 'picker',
          });
        }

        await uploadManager.uploadExternalUrls(
          {
            libraryId: destinationLibraryId,
            folderPath: newLibrary ? FOLDER_ROOT_PATH : folderPath,
          },
          nonDuplicateSelectedItems,
          {
            onProgress: (completed, total) => {
              if (debugStore.smallBatchSizeMode) {
                console.log('[Upload Progress]', {
                  completed,
                  total,
                  percentage: Math.round((completed / total) * 100),
                });
              }
              setUploadProgress({ completed, total });
            },
            signal: abortController.signal,
            libraryName: newLibrary ? newLibrary.name : library.data?.name,
          },
        );
        setUploadProgress(null);

        if (newLibrary && newLibrary._id) {
          navigate(getLibraryUrl(newLibrary._id, newLibrary.name));
        } else if (isFirstTime && !newLibrary && libraryManager.defaultLibrary) {
          navigate(
            getLibraryUrl(
              libraryManager.defaultLibrary.libraryId,
              libraryManager.defaultLibrary.name,
            ),
          );
        } else if (!location.pathname.startsWith(`/library/${library.libraryId}`)) {
          // Existing destination — may differ from the page we're on (the picker
          // can target any library). Skip when already there so we don't reset the
          // library URL / drop active query params.
          navigate(getLibraryUrl(library.libraryId, library.data?.name || ''));
        }

        // Refresh the library to show the new uploads
        uploadManager.refreshLibrary(library.libraryId);

        // Refetch credits after upload completion
        await billingStore.refetchCreditsAfterOperation();

        closeModal?.();
      } catch (error) {
        console.error('Error uploading selected videos:', error);
        setUploadProgress(null);

        // Don't show error or close modals for cancelled uploads
        if (error instanceof Error && error.name === 'AbortError') {
          showToast('media-import-cancelled');

          // Refresh the library to show partial uploads and refetch credits
          uploadManager.refreshLibrary(library.libraryId);
          await billingStore.refetchCreditsAfterOperation();

          closeModal?.();
          return;
        }

        closeModal?.();
      }
    }

    // The "already auto-opened" latch lives on the store (`pickerAutoOpened`), which also re-arms
    // it when the raw input changes. It used to be component state reset by an effect here, but
    // this component remounts when the destination library changes — which re-armed the latch and
    // reopened the picker over surviving entries.

    // Auto-open ChooseMediaModal when all URLs are checked and more than one video is found
    useEffect(() => {
      const allUrlsChecked =
        uploadURLStore.urlEntries.length > 0 && uploadURLStore.loadingEntries.length === 0;
      const hasValidEntries = uploadURLStore.validEntries.length > 0;
      const allEnhancementComplete = !uploadURLStore.isAnyEnhancing;
      const totalVideoCount = uploadURLStore.allEnhancedVideos.length;

      const shouldAutoOpen =
        allUrlsChecked &&
        hasValidEntries &&
        allEnhancementComplete &&
        totalVideoCount > 1 &&
        // Never re-open over a picker that's already up. Editing URLs from inside the picker
        // rewrites rawInput, and every entry it adds churns the deps below — without this each
        // change would call open() again, and each open() re-snapshots existingUniqueIds,
        // invalidating the frozen selection state that useUrlUploadFlow keeps to stop the grid
        // redrawing and losing scroll position mid-browse.
        urlUploadFlow.flowState === 'idle' &&
        !uploadURLStore.pickerAutoOpened;

      if (shouldAutoOpen) {
        // Latch at the decision point rather than in ChooseMediaModal's mount callback. The
        // modal doesn't remount, so a rawInput change while it's open re-arms the latch via the
        // store's reaction with no second onOpen to set it again — which would leave the picker
        // free to reopen unprompted after the user dismissed it.
        uploadURLStore.setPickerAutoOpened(true);
        urlUploadFlow.open();
      }
    }, [
      uploadURLStore.urlEntries.length,
      uploadURLStore.loadingEntries.length,
      uploadURLStore.validEntries.length,
      uploadURLStore.isAnyEnhancing,
      uploadURLStore.allEnhancedVideos.length,
      uploadURLStore.rawInput,
      uploadURLStore.pickerAutoOpened,
      urlUploadFlow.flowState,
    ]);

    const handleUploadUrl = async ({
      newLibraryName,
      createNewLibrary,
    }: {
      newLibraryName?: string;
      createNewLibrary: boolean;
    }) => {
      const result = await uploadURLStore.handleUploadUrl(
        libraryManager,
        uploadManager,
        library,
        createNewLibrary,
        newLibraryName,
        folderPath,
      );

      if (result.success) {
        if (createNewLibrary && result.libraryId) {
          navigate(getLibraryUrl(result.libraryId, result.libraryName));
        } else if (isFirstTime && !result.libraryId && libraryManager.defaultLibrary) {
          navigate(
            getLibraryUrl(
              libraryManager.defaultLibrary.libraryId,
              libraryManager.defaultLibrary.name,
            ),
          );
        } else if (!location.pathname.startsWith(`/library/${library.libraryId}`)) {
          // Existing destination — may differ from the page we're on. Skip when
          // already there to avoid resetting the library URL / query params.
          navigate(getLibraryUrl(library.libraryId, library.data?.name || ''));
        }
        // No need to re-arm the latch here: a successful upload already cleared the input via
        // `setRawInput('')` in the store, and the reaction on `rawInput` re-arms it.
        closeModal?.();
      }
    };

    const handleUploadButtonClick = () => {
      // Require a new-library name before proceeding (native validation).
      if (validateBeforeProceed && !validateBeforeProceed()) return;
      const validEntries = uploadURLStore.validEntries;
      const allVideos = unfurledItems;
      const totalVideoCount = allVideos.length;

      const estimate = estimateIngest(allVideos);
      const buttonLabel = totalVideoCount > 1 ? 'Choose media to add' : 'Add media';
      trackingStore.addMediaClicked({
        libraryId: library.libraryId,
        videoCount: totalVideoCount,
        totalDurationSeconds: estimate.durationSeconds,
        creditsCost: estimate.credits,
        creditsAvailable: billingStore.creditsLeft ?? 0,
        buttonLabel,
      });

      // Multiple URLs or multiple videos -> open ChooseMediaModal
      if (validEntries.length > 1 || totalVideoCount > 1) {
        // Special case: if there's exactly one video total, upload directly
        if (totalVideoCount === 1 && allVideos[0]) {
          handleChooseMediaUploadButtonClick(
            [
              {
                url: allVideos[0].url,
                name: allVideos[0].title || 'Untitled',
              },
            ],
            {
              createNewLibrary: shouldCreateNewLibrary,
              newLibraryName: newLibraryName || DEFAULT_LIBRARY_NAME,
            },
          );
          return;
        }

        urlUploadFlow.open();
      } else if (validEntries.length === 1) {
        const firstEntry = validEntries[0];
        if (
          firstEntry.unfurlData &&
          (isYoutubeMultipleData(firstEntry.unfurlData) ||
            isFacebookAdsMultipleData(firstEntry.unfurlData))
        ) {
          handleUploadUrl({ createNewLibrary: shouldCreateNewLibrary, newLibraryName });
        } else {
          handleUploadUrl({ createNewLibrary: shouldCreateNewLibrary, newLibraryName });
        }
      } else {
        handleUploadUrl({ createNewLibrary: shouldCreateNewLibrary, newLibraryName });
      }
    };

    const handleUploadAllVideosClick = () => {
      if (unfurledItems.length > 0) {
        if ('id' in unfurledItems[0]) {
          const allVideoIds = unfurledItems.map((video) => video.id);
          urlUploadFlow.setSelection(allVideoIds);
        }
        urlUploadFlow.advanceToConfirm();
      }
    };

    const handleChooseMediaModalClose = () => {
      // Only reset to idle if still selecting (user manually closed).
      // Don't reset if we advanced to confirming — that would kill the summary.
      if (urlUploadFlow.flowState === 'selecting') {
        urlUploadFlow.close();
      }
    };

    return (
      <>
        <MediaUploadBox
          className={className}
          onUploadButtonClick={handleUploadButtonClick}
          handleUploadAllVideosClick={handleUploadAllVideosClick}
          closeModal={closeModal}
          externalIsDragging={externalIsDragging}
          isFirstTime={isFirstTime}
          createNewLibrary={createNewLibrary}
          newLibraryName={newLibraryName}
          footerPortalTarget={footerPortalTarget}
          validateBeforeProceed={validateBeforeProceed}
          autoFocus={autoFocus}
          compact={compact}
          folderPath={folderPath}
        />
        {urlUploadFlow.flowState === 'selecting' && (
          <ChooseMediaModal
            {...urlUploadFlow.selectionProps}
            onOpen={() => uploadURLStore.setPickerAutoOpened(true)}
            onClose={handleChooseMediaModalClose}
          />
        )}
        {urlUploadFlow.flowState === 'confirming' && (
          <ChooseMediaModalSummaryConfirm {...urlUploadFlow.summaryProps} />
        )}
      </>
    );
  },
);
